import type { ChannelAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'
import type { ChannelType, ChannelVisibility } from '@/client/services/worlds'
import { EDITABLE_POST_TYPES } from '@/client/services/worlds/constants'

import ChannelMemberQ from './ChannelMemberQ'
import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import PostQ from './PostQ'
import WorldQ from './WorldQ'

const ChannelQ = {
  ...makeQueryModelV2<ChannelAttributes>('channel')([
    'id',
    'world_id',
    'title',
    'description',
    'position',
    'type',
    'visibility',
    'world_level',
    'archived_at',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    World: {
      query: WorldQ,
      foreignKey: WorldQ.id,
      sourceKey: ChannelQ.world_id,
    },
    ChannelMembers: {
      query: ChannelMemberQ,
      foreignKey: ChannelMemberQ.channel_id,
      sourceKey: ChannelQ.id,
    },
  }),

  where: (
    {
      id,
      worldId,
      type,
      isGlobal,
      groupBy = null,
      include = [],
      order = null,
      paranoid = true,
    }: {
      id?: number | null | SQLFragment<number>
      worldId?: number | null | SQLFragment<number>
      type?: ChannelType | null
      isGlobal?: boolean | null
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      include?: IncludeAssociations
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<ChannelAttributes> => SQL`
        FROM ${ChannelQ}
        ${includeAssociationsToSql(ChannelQ, include)}
        WHERE
            ${id != null ? SQL`${ChannelQ.id} = ${id} AND` : SQL``}
            ${worldId != null ? SQL`${ChannelQ.world_id} = ${worldId} AND` : SQL``}
            ${type != null ? SQL`${ChannelQ.type} = ${type} AND` : SQL``}
            ${isGlobal != null ? SQL`${ChannelQ.world_id} IS NULL AND` : SQL``}
            ${paranoid ? SQL`${ChannelQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
    `,

  hasVisibility: (visibility: ChannelVisibility): SQLFragment<boolean> =>
    SQL`${ChannelQ.visibility} = ${visibility}`,

  isUserMember: (userId: number | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${ChannelMemberQ.where({ channelId: ChannelQ.id, userId })})`,

  isVisibleToUser: ({
    userId,
    allowArchived = false,
  }: {
    userId: number | SQLFragment<number>
    allowArchived?: boolean
  }): SQLFragment<boolean> =>
    SQL`((${ChannelQ.isUserMember(userId)} OR ${ChannelQ.hasVisibility('public')}))
      AND ${allowArchived ? SQL`TRUE` : SQL`${ChannelQ.archived_at} IS NULL`}`,

  countMembers: (): SQLFragment<number> =>
    SQL`COUNT(*) ${ChannelMemberQ.where({ channelId: ChannelQ.id })}`,

  hasType: (type: ChannelType): SQLFragment<boolean> => SQL`${ChannelQ.type} = ${type}`,

  countPosts: (): SQLFragment<number> =>
    SQL`COUNT(*) ${PostQ.where(
      {
        channelId: ChannelQ.id,
      },
      SQL`${PostQ.type} = ANY(${EDITABLE_POST_TYPES})`
    )}`,
}

export default ChannelQ
